import FeatherIcon from "feather-icons-react";
import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState, useCallback } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  Modal,
  Col,
  Row,
  Spinner,
  Input,
  Button,
} from "reactstrap";
import api from "../helpers/api";
import { formatCreatedDate, formatEnumName, countryOptions, vendorTypeTranslations } from "../constants/utils";
import { getPresignedUrl } from "../helpers/fileUpload";
import * as XLSX from "xlsx";
import FileModal from "../components/Common/importExportModal";
import VendorModal from "../components/VendorModal";
import Select from "react-select";
import PermissionGuard from "../components/Common/PermissionGuard";
import usePermission from "../Hooks/usePermission";
import { useTranslation } from 'react-i18next';

// The vendor's primary contact (flagged one, else the first), or null when there are none.
const getPrimaryContact = (vendor) =>
  vendor?.contacts?.find((c) => c.primary) || vendor?.contacts?.[0] || null;

// "+<code> <number>" for a contact, or "" when there's no number.
const formatContactPhone = (contact) =>
  contact?.phoneNumber
    ? `${contact.phoneCode ? `+${contact.phoneCode} ` : ""}${contact.phoneNumber}`
    : "";

const Vendor = () => {
  const { t } = useTranslation();
  document.title = `${t("shared.vendors", "Vendors")} | ArcMedix`;

  const currentUser = JSON.parse(localStorage.getItem('authUser'));
  const { isAdmin } = usePermission();

  // Check if user can delete/edit a vendor (must be owner or admin)
  const canModifyVendor = (vendor) => {
    if (isAdmin) return true;
    // createdBy can be an object with id/name or just an id
    const createdById = vendor?.createdBy?.id ?? vendor?.createdBy;
    const userId = currentUser?.id;
    // If createdBy not present (backend not updated), allow based on permission only
    if (createdById === undefined || createdById === null) return true;
    return createdById === userId;
  };

  const [vendorModal, setVendorModal] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [count, setCount] = useState(10);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [fileModal, setFileModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [vendorDetailsModal, setVendorDetailsModal] = useState(false);
  const [selectedVendorForDetails, setSelectedVendorForDetails] = useState(null);

  // Document upload handlers
  const handleFileChange = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    try {
      const uploadedUrls = [];

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const publicURL = await getPresignedUrl(file);
        if (publicURL) {
          uploadedUrls.push(publicURL);
        }
      }

      setSelectedVendor(prev => ({
        ...prev,
        documents: [...(prev?.documents || []).filter(doc => doc !== ""), ...uploadedUrls]
      }));
    } catch (err) {
      setApiError(t("shared.error_during_upload", "Error during upload: ") + err.message);
    }
  };


  const removeDocument = (indexToRemove) => {
    setSelectedVendor(prev => ({
      ...prev,
      documents: prev?.documents?.filter((_, index) => index !== indexToRemove) || []
    }));
  };

  // Contact-people row handlers (add/remove/update/set-primary). Exactly one contact
  // is primary while the list is non-empty; the server normalizes as a backstop.
  const emptyContact = () => ({ fullName: "", role: "", email: "", phoneCode: "", phoneNumber: "", primary: false });

  const addContact = () => {
    setSelectedVendor(prev => {
      const contacts = prev?.contacts || [];
      return { ...prev, contacts: [...contacts, { ...emptyContact(), primary: contacts.length === 0 }] };
    });
  };

  const removeContact = (index) => {
    setSelectedVendor(prev => {
      let contacts = (prev?.contacts || []).filter((_, i) => i !== index);
      // If we removed the primary, promote the first remaining contact.
      if (contacts.length > 0 && !contacts.some((c) => c.primary)) {
        contacts = contacts.map((c, i) => ({ ...c, primary: i === 0 }));
      }
      return { ...prev, contacts };
    });
  };

  const updateContact = (index, field, value) => {
    setSelectedVendor(prev => ({
      ...prev,
      contacts: (prev?.contacts || []).map((c, i) => (i === index ? { ...c, [field]: value } : c))
    }));
  };

  const setPrimaryContact = (index) => {
    setSelectedVendor(prev => ({
      ...prev,
      contacts: (prev?.contacts || []).map((c, i) => ({ ...c, primary: i === index }))
    }));
  };

  // Toggle vendor modal
  function toggleVendorModal(vendor = null) {
    setVendorModal(!vendorModal);
    setSelectedVendor(vendor ? { ...vendor, contacts: vendor.contacts || [] } : {});
    setErrors({});
    setApiError("");
  }

  // Toggle vendor details modal
  const toggleVendorDetailsModal = (vendor = null) => {
    setVendorDetailsModal(!vendorDetailsModal);
    setSelectedVendorForDetails(vendor);
  };

  const handleVendorModalClose = () => {
    setVendorModal(false);
    setSelectedVendor(null);
  };

  const handleVendorUpdate = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setApiError("");
    setErrors({});

    // Validation
    const newErrors = {};
    if (!selectedVendor?.vendorName?.trim()) {
      newErrors.vendorName = t("business.vendors.validation.vendor_name_required", "Vendor name is required");
    }
    if (!selectedVendor?.vendorType?.trim()) {
      newErrors.vendorType = t("business.vendors.validation.vendor_type_required", "Vendor type is required");
    }
    if (!selectedVendor?.email?.trim()) {
      newErrors.email = t("shared.email_is_required", "Email is required");
    } else if (!/\S+@\S+\.\S+/.test(selectedVendor.email)) {
      newErrors.email = t("business.vendors.validation.email_invalid", "Email is invalid");
    }
    if (!selectedVendor?.phoneNumber?.trim()) {
      newErrors.phoneNumber = t("business.vendors.validation.phone_required", "Phone number is required");
    }
    if (!selectedVendor?.address?.trim()) {
      newErrors.address = t("shared.address_is_required", "Address is required");
    }
    (selectedVendor?.contacts || []).forEach((c, i) => {
      if (!c.fullName?.trim()) {
        newErrors[`contact_${i}_fullName`] = t("business.vendors.contacts.validation.name_required", "Contact name is required");
      }
      if (c.email?.trim() && !/\S+@\S+\.\S+/.test(c.email)) {
        newErrors[`contact_${i}_email`] = t("business.vendors.validation.email_invalid", "Email is invalid");
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setIsSubmitting(false);
      return;
    }

    try {
      // Separate phoneCode and phoneNumber
      const phoneCode = selectedVendor?.phoneCode || "";
      const fullPhoneNumber = selectedVendor?.phoneNumber || "";
      const phoneNumber = fullPhoneNumber.replace(phoneCode, "");

      const vendorData = {
        vendorName: selectedVendor.vendorName,
        vendorType: selectedVendor.vendorType,
        email: selectedVendor.email,
        phoneCode: phoneCode,
        phoneNumber: phoneNumber,
        address: selectedVendor.address,
        companyIdNumber: selectedVendor.companyIdNumber || null,
        remarks: selectedVendor.remarks || "",
        documents: selectedVendor.documents || [],
        // Always send the array (empty allowed) so the backend replace-all persists removals.
        // Contact phoneNumber is digits-only with phoneCode kept separate (no concatenation).
        contacts: (selectedVendor.contacts || []).map((c) => ({
          fullName: c.fullName?.trim(),
          role: c.role?.trim() || null,
          email: c.email?.trim() || null,
          phoneCode: c.phoneCode || null,
          phoneNumber: c.phoneNumber || null,
          primary: !!c.primary,
        })),
      };

      if (selectedVendor?.id) {
        // Update existing vendor
        const updateData = {
          id: selectedVendor.id,
          ...vendorData
        };
        await api.put("vendor/update-Vendor", updateData);
      } else {
        // Create new vendor
        await api.post("vendor/create-Vendor", vendorData);
      }

      handleVendorModalClose();
      reloadVendors();
    } catch (error) {
      console.error("Error during vendor submission:", error);
      setApiError(error?.response?.data?.message || t("shared.error_submission", "An error occurred during submission"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    try {
      if (selectedVendor?.id) {
        await api.delete(`vendor/delete-Vendor?id=${selectedVendor.id}`);
        reloadVendors();
        setDeleteModal(false);
      }
    } catch (error) {
      console.error("Error deleting vendor:", error);
      setApiError(t("business.vendors.delete_error", "Failed to delete vendor"));
    }
  };

  const handleSearch = (e) => {
    const term = e.target.value;
    setSearchTerm(term);
    setVendors([]);
    setCount(10);
    setHasMore(true);
  };

  const handleImport = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const data = new Uint8Array(e.target.result);
      const workbook = XLSX.read(data, { type: "array" });
      const firstSheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[firstSheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet);
    };
    reader.readAsArrayBuffer(file);
  };

  const handleExport = () => {
    const dataToExport =
      vendors?.map((vendor, index) => {
        const primary = getPrimaryContact(vendor);
        return {
          "#": index + 1,
          [t("shared.vendor_name", "Vendor Name")]: vendor?.vendorName || "",
          [t("shared.vendor_type", "Vendor Type")]: vendorTypeTranslations(t)[vendor?.vendorType] || vendor?.vendorType || "",
          [t("shared.email", "Email")]: vendor?.email || "",
          [t("shared.phone_number", "Phone Number")]: vendor?.phoneNumber || "",
          [t("shared.address", "Address")]: vendor?.address || "",
          [t("business.vendors.contacts.primary_contact", "Primary contact")]: primary?.fullName || "",
          [t("business.vendors.contacts.job_title", "Job title")]: primary?.role || "",
          [t("business.vendors.contacts.contact_email", "Contact email")]: primary?.email || "",
          [t("business.vendors.contacts.contact_phone", "Contact phone")]: formatContactPhone(primary),
          [t("shared.remarks", "Remarks")]: vendor?.remarks || "",
          [t("shared.created_at", "Created At")]: formatCreatedDate(vendor?.createdAt) || "",
        };
      }) || [];

    const ws = XLSX.utils.json_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, t("shared.vendors", "Vendors"));
    XLSX.writeFile(
      wb,
      `vendors_export_${new Date().toISOString().split("T")[0]}.xlsx`
    );
  };

  const vendorTypes = ['SERVICE_PROVIDER', 'SPARE_PARTS_SUPPLIER', 'OEM', 'DISTRIBUTOR'];

  const reloadVendors = useCallback(async () => {
    setLoading(true);
    setCount(10);
    try {
      const { data } = await api.get(
        `vendor/getAllVendors?search=${searchTerm}`
      );
      setVendors(data.response || []);
      setHasMore(data.response.length >= 10);
    } catch (error) {
      console.error("Error reloading vendors:", error);
    } finally {
      setLoading(false);
    }
  }, [searchTerm]);

  const loadMoreVendors = async () => {
    if (loading || !hasMore) return;

    setLoading(true);
    try {
      const newCount = count + 10;
      const { data } = await api.get(
        `vendor/getAllVendors?search=${searchTerm}&count=${newCount}`
      );
      
      setVendors(data.response || []);
      setCount(newCount);
      setHasMore(data.response.length === newCount);
    } catch (error) {
      console.error("Error loading more vendors:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reloadVendors();
  }, [reloadVendors]);

  return (
    <div className="page-content mt-4">
      <div className="mb-4 d-flex align-items-center gap-3">
        <motion.div
          className="mb-4 d-flex align-items-center gap-3"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <PermissionGuard permission="vendor:create">
            <button
              type="button"
              className="btn btn-soft-success waves-effect waves-light d-flex align-items-center gap-2 transition-all hover-shake"
              onClick={() => {
                toggleVendorModal();
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="currentColor"
                height={21}
                width={21}
              >
                <path d="M5.25 6.375a4.125 4.125 0 1 1 8.25 0 4.125 4.125 0 0 1-8.25 0ZM2.25 19.125a7.125 7.125 0 0 1 14.25 0v.003l-.001.119a.75.75 0 0 1-.363.63 13.067 13.067 0 0 1-6.761 1.873c-2.472 0-4.786-.684-6.76-1.873a.75.75 0 0 1-.364-.63l-.001-.122ZM18.75 7.5a.75.75 0 0 0-1.5 0v2.25H15a.75.75 0 0 0 0 1.5h2.25v2.25a.75.75 0 0 0 1.5 0v-2.25H21a.75.75 0 0 0 0-1.5h-2.25V7.5Z" />
              </svg>
              {t("business.vendors.add_vendor", "Add Vendor")}
            </button>
          </PermissionGuard>
          <div
            className="ms-auto"
            style={{ minWidth: "400px", flexGrow: 1, maxWidth: "500px" }}
          >
            <Input
              type="text"
              placeholder={t("business.vendors.search_placeholder", "Search Vendors...")}
              value={searchTerm}
              onChange={handleSearch}
            />
          </div>
        </motion.div>
      </div>
      
      <Card>
        <CardHeader className="d-flex justify-content-between align-items-center">
          <h3 className="card-title text-success">{t("shared.vendors", "Vendors")}</h3>
          <div className="d-flex align-items-center gap-2">
            <button
              className="btn btn-soft-info p-2"
              onClick={() => setFileModal(true)}
              title={t("business.vendors.import_export_title", "Import/Export vendors")}
            >
              <FeatherIcon icon="file-text" size={18} />
            </button>
          </div>
        </CardHeader>
        <CardBody>
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead>
                <tr>
                  <th>#</th>
                  <th>{t("shared.vendor_name", "Vendor Name")}</th>
                  <th>{t("business.vendors.contacts.primary_contact", "Primary contact")}</th>
                  <th>{t("shared.delete", "Delete")}</th>
                </tr>
              </thead>
              <tbody>
                <AnimatePresence>
                  {vendors?.map((vendor, ind) => (
                    <motion.tr
                      key={vendor.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ duration: 0.3 }}
                    >
                      <th scope="row">{ind + 1}</th>
                      <td>
                        <span
                          className="text-primary cursor-pointer hover-underline"
                          onClick={() => toggleVendorDetailsModal(vendor)}
                          style={{ cursor: 'pointer' }}
                        >
                          {vendor.vendorName}
                        </span>
                      </td>
                      <td>
                        {(() => {
                          const pc = getPrimaryContact(vendor);
                          if (!pc) {
                            return <span className="text-muted">{t("shared.n_a", "N/A")}</span>;
                          }
                          const detail = [pc.email, formatContactPhone(pc)].filter(Boolean).join(" · ");
                          return (
                            <div>
                              <div className="fw-medium">{pc.fullName}</div>
                              {detail && <small className="text-muted">{detail}</small>}
                            </div>
                          );
                        })()}
                      </td>
                      <td>
                        {canModifyVendor(vendor) && (
                          <PermissionGuard permission="vendor:delete">
                            <span className='text-danger' role='button' onClick={() => {
                              setSelectedVendor(vendor);
                              setDeleteModal(true);
                            }}>
                              <FeatherIcon icon="trash-2" />
                            </span>
                          </PermissionGuard>
                        )}
                      </td>
                    </motion.tr>
                  ))}
                </AnimatePresence>
              </tbody>
            </table>
          </div>

          {loading && (
            <div className="text-center py-3">
              <Spinner color="primary" />
            </div>
          )}

          <div className="text-center mt-3">
            {hasMore && vendors.length > 0 ? (
              <Button
                color="primary"
                onClick={loadMoreVendors}
                disabled={loading}
              >
                {loading ? t("shared.loading", "Loading...") : t("shared.load", "Load More")}
              </Button>
            ) : (
              vendors.length > 0 && (
                <div className="text-muted">{t("business.vendors.no_more_vendors", "No more vendors to load")}</div>
              )
            )}
          </div>

          {vendors.length === 0 && !loading && (
            <div className="text-center py-5 text-muted">
              {searchTerm
                ? t("business.vendors.no_search_results", "No vendors match your search")
                : t("shared.no_vendors_found", "No vendors found")}
            </div>
          )}
        </CardBody>
      </Card>

      {/* Vendor Modal */}
      <Modal
        size="lg"
        isOpen={vendorModal}
        toggle={() => {
          toggleVendorModal();
        }}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          <div className="modal-header">
            <h5 className="modal-title" id="myLargeModalLabel">
              {selectedVendor?.id ? t("shared.edit_vendor", "Edit Vendor") : t("business.vendors.add_vendor", "Add Vendor")}
            </h5>
            <button
              onClick={handleVendorModalClose}
              type="button"
              className="btn-close"
              data-dismiss="modal"
              aria-label={t("shared.close", "Close")}
            ></button>
          </div>
          <div className="modal-body">
            <form onSubmit={handleVendorUpdate}>
              <Row className="mb-4">
                <label
                  htmlFor="horizontal-vendorname-input"
                  className="col-sm-3 col-form-label"
                >
                  {t("shared.vendor_name", "Vendor Name")}
                </label>
                <Col sm={9}>
                  <input
                    type="text"
                    value={selectedVendor?.vendorName || ""}
                    onChange={(e) =>
                      setSelectedVendor({
                        ...selectedVendor,
                        vendorName: e.target.value,
                      })
                    }
                    className={`form-control ${
                      errors.vendorName ? "is-invalid" : ""
                    }`}
                    placeholder={t("business.vendors.form.enter_vendor_name", "Enter vendor name")}
                    id="horizontal-vendorname-input"
                  />
                  {errors.vendorName && (
                    <small className="text-danger">{errors.vendorName}</small>
                  )}
                </Col>
              </Row>

              <Row className="mb-4">
                <label
                  htmlFor="horizontal-vendortype-input"
                  className="col-sm-3 col-form-label"
                >
                  {t("shared.vendor_type", "Vendor Type")}
                </label>
                <Col sm={9}>
                  <select
                    value={selectedVendor?.vendorType || ""}
                    onChange={(e) =>
                      setSelectedVendor({
                        ...selectedVendor,
                        vendorType: e.target.value,
                      })
                    }
                    className={`form-select ${
                      errors.vendorType ? "is-invalid" : ""
                    }`}
                    id="horizontal-vendortype-input"
                  >
                    <option value="">{t("business.vendors.form.select_vendor_type", "Select vendor type")}</option>
                    {vendorTypes?.map((vendorType) => (
                      <option key={vendorType} value={vendorType}>
                        {vendorTypeTranslations(t)[vendorType] || formatEnumName(vendorType)}
                      </option>
                    ))}
                  </select>
                  {errors.vendorType && (
                    <small className="text-danger">{errors.vendorType}</small>
                  )}
                </Col>
              </Row>

              <Row className="mb-4">
                <label
                  htmlFor="horizontal-email-input"
                  className="col-sm-3 col-form-label"
                >
                  {t("shared.email", "Email")}
                </label>
                <Col sm={9}>
                  <input
                    type="email"
                    value={selectedVendor?.email || ""}
                    onChange={(e) =>
                      setSelectedVendor({
                        ...selectedVendor,
                        email: e.target.value,
                      })
                    }
                    className={`form-control ${
                      errors.email ? "is-invalid" : ""
                    }`}
                    placeholder={t("shared.enter_email_address", "Enter email address")}
                    id="horizontal-email-input"
                  />
                  {errors.email && (
                    <small className="text-danger">{errors.email}</small>
                  )}
                </Col>
              </Row>

              <Row className="mb-4">
                <label
                  htmlFor="horizontal-phone-input"
                  className="col-sm-3 col-form-label"
                >
                  {t("shared.phone_number", "Phone Number")}
                </label>
                <Col sm={9}>
                  <div className="d-flex gap-2">
                    {/* Country Code Select */}
                    <Select
                      options={countryOptions}
                      value={countryOptions.find((c) =>
                        selectedVendor?.phoneCode === c.dialCode.replace("+", "")
                      )}
                      onChange={(selected) => {
                        const dialCode = selected?.dialCode.replace("+", "") || "";
                        const phoneNumber = selectedVendor?.phoneNumber?.replace(/\D/g, "") || "";

                        setSelectedVendor({
                          ...selectedVendor,
                          phoneCode: dialCode,
                          phoneNumber: dialCode + phoneNumber
                        });
                      }}
                      filterOption={(option, inputValue) => {
                        if (!inputValue) return true;
                        const searchText = inputValue.toLowerCase();
                        const labelText = option.data.labelText?.toLowerCase() || '';
                        const name = option.data.name?.toLowerCase() || '';
                        const dialCode = option.data.dialCode?.toLowerCase() || '';
                        return labelText.includes(searchText) ||
                               name.includes(searchText) ||
                               dialCode.includes(searchText);
                      }}
                      className="flex-grow-0"
                      styles={{
                        control: (base) => ({
                          ...base,
                          width: "150px",
                        }),
                      }}
                      isClearable
                      placeholder={t("shared.code", "Code")}
                    />

                    {/* Phone Number Input */}
                    <input
                      type="tel"
                      className={`form-control flex-grow-1 ${
                        errors.phoneNumber ? "is-invalid" : ""
                      }`}
                      value={selectedVendor?.phoneNumber?.replace(/\D/g, "").replace(selectedVendor?.phoneCode || "", "") || ""}
                      onChange={(e) => {
                        const phone = e.target.value.replace(/\D/g, "");
                        const dialCode = selectedVendor?.phoneCode || "";
                        setSelectedVendor({
                          ...selectedVendor,
                          phoneNumber: dialCode + phone
                        });
                      }}
                      placeholder={t("shared.phone_number", "Phone number")}
                      id="horizontal-phone-input"
                    />
                  </div>
                  {errors.phoneNumber && (
                    <small className="text-danger">{errors.phoneNumber}</small>
                  )}
                </Col>
              </Row>

              <Row className="mb-4">
                <label
                  htmlFor="horizontal-company-id-input"
                  className="col-sm-3 col-form-label"
                >
                  {t("planning_purchasing.company_id", "Company ID")}
                </label>
                <Col sm={9}>
                  <input
                    id="horizontal-company-id-input"
                    type="text"
                    className="form-control"
                    value={selectedVendor?.companyIdNumber || ""}
                    onChange={(e) =>
                      setSelectedVendor({
                        ...selectedVendor,
                        companyIdNumber: e.target.value,
                      })
                    }
                  />
                  <small className="text-muted">
                    {t("shared.vendor_company_id_hint", "Prefills the vendor identification on purchase orders.")}
                  </small>
                </Col>
              </Row>

              <Row className="mb-4">
                <label
                  htmlFor="horizontal-address-input"
                  className="col-sm-3 col-form-label"
                >
                  {t("shared.address", "Address")}
                </label>
                <Col sm={9}>
                  <textarea
                    value={selectedVendor?.address || ""}
                    onChange={(e) =>
                      setSelectedVendor({
                        ...selectedVendor,
                        address: e.target.value,
                      })
                    }
                    className={`form-control ${
                      errors.address ? "is-invalid" : ""
                    }`}
                    placeholder={t("business.vendors.form.enter_address", "Enter address")}
                    id="horizontal-address-input"
                    rows={3}
                  />
                  {errors.address && (
                    <small className="text-danger">{errors.address}</small>
                  )}
                </Col>
              </Row>

              <Row className="mb-4">
                <label
                  htmlFor="horizontal-remarks-input"
                  className="col-sm-3 col-form-label"
                >
                  {t("shared.remarks", "Remarks")}
                </label>
                <Col sm={9}>
                  <textarea
                    value={selectedVendor?.remarks || ""}
                    onChange={(e) =>
                      setSelectedVendor({
                        ...selectedVendor,
                        remarks: e.target.value,
                      })
                    }
                    className={`form-control ${
                      errors.remarks ? "is-invalid" : ""
                    }`}
                    placeholder={t("business.vendors.form.enter_remarks", "Enter remarks")}
                    id="horizontal-remarks-input"
                    rows={3}
                  />
                  {errors.remarks && (
                    <small className="text-danger">{errors.remarks}</small>
                  )}
                </Col>
              </Row>

              <Row className="mb-4">
                <label className="col-sm-3 col-form-label">
                  {t("business.vendors.contacts.title", "Contacts")}
                </label>
                <Col sm={9}>
                  {(selectedVendor?.contacts || []).map((contact, index) => (
                    <div key={index} className="border rounded p-3 mb-2 bg-light">
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <div className="form-check mb-0">
                          <input
                            className="form-check-input"
                            type="radio"
                            name="primaryContact"
                            id={`primary-contact-${index}`}
                            checked={!!contact.primary}
                            onChange={() => setPrimaryContact(index)}
                          />
                          <label className="form-check-label fw-medium" htmlFor={`primary-contact-${index}`}>
                            {t("business.vendors.contacts.primary", "Primary")}
                          </label>
                        </div>
                        <button
                          type="button"
                          className="btn btn-outline-danger btn-sm"
                          onClick={() => removeContact(index)}
                          title={t("shared.remove", "Remove")}
                        >
                          <FeatherIcon icon="trash-2" size={14} />
                        </button>
                      </div>
                      <Row>
                        <Col md={6}>
                          <div className="form-group mb-3">
                            <label className="form-label fw-medium">
                              {t("shared.full_name", "Full Name")} <span className="text-danger">*</span>
                            </label>
                            <input
                              type="text"
                              className={`form-control ${errors[`contact_${index}_fullName`] ? "is-invalid" : ""}`}
                              value={contact.fullName || ""}
                              onChange={(e) => updateContact(index, "fullName", e.target.value)}
                            />
                            {errors[`contact_${index}_fullName`] && (
                              <small className="text-danger">{errors[`contact_${index}_fullName`]}</small>
                            )}
                          </div>
                        </Col>
                        <Col md={6}>
                          <div className="form-group mb-3">
                            <label className="form-label fw-medium">
                              {t("business.vendors.contacts.job_title", "Job title")}
                            </label>
                            <input
                              type="text"
                              className="form-control"
                              value={contact.role || ""}
                              onChange={(e) => updateContact(index, "role", e.target.value)}
                            />
                          </div>
                        </Col>
                        <Col md={6}>
                          <div className="form-group mb-3">
                            <label className="form-label fw-medium">{t("shared.email", "Email")}</label>
                            <input
                              type="email"
                              className={`form-control ${errors[`contact_${index}_email`] ? "is-invalid" : ""}`}
                              value={contact.email || ""}
                              onChange={(e) => updateContact(index, "email", e.target.value)}
                            />
                            {errors[`contact_${index}_email`] && (
                              <small className="text-danger">{errors[`contact_${index}_email`]}</small>
                            )}
                          </div>
                        </Col>
                        <Col md={6}>
                          <div className="form-group mb-3">
                            <label className="form-label fw-medium">{t("shared.phone_number", "Phone Number")}</label>
                            <div className="d-flex gap-2">
                              <Select
                                options={countryOptions}
                                value={countryOptions.find((c) => contact.phoneCode === c.dialCode.replace("+", "")) || null}
                                onChange={(selected) =>
                                  updateContact(index, "phoneCode", selected?.dialCode.replace("+", "") || "")
                                }
                                filterOption={(option, inputValue) => {
                                  if (!inputValue) return true;
                                  const searchText = inputValue.toLowerCase();
                                  const labelText = option.data.labelText?.toLowerCase() || "";
                                  const name = option.data.name?.toLowerCase() || "";
                                  const dialCode = option.data.dialCode?.toLowerCase() || "";
                                  return labelText.includes(searchText) || name.includes(searchText) || dialCode.includes(searchText);
                                }}
                                styles={{ control: (base) => ({ ...base, width: "120px" }) }}
                                isClearable
                                placeholder={t("shared.code", "Code")}
                              />
                              <input
                                type="tel"
                                className="form-control flex-grow-1"
                                value={contact.phoneNumber || ""}
                                onChange={(e) => updateContact(index, "phoneNumber", e.target.value.replace(/\D/g, ""))}
                              />
                            </div>
                          </div>
                        </Col>
                      </Row>
                    </div>
                  ))}
                  <Button type="button" color="primary" outline size="sm" onClick={addContact}>
                    <FeatherIcon icon="plus" size={14} className="me-1" />
                    {t("business.vendors.contacts.add_contact", "Add contact")}
                  </Button>
                </Col>
              </Row>

              <Row className="mb-4">
                <label htmlFor="documentsUpload" className="col-sm-3 col-form-label">
                  {t("shared.documents", "Documents")}
                </label>
                <Col sm={9}>
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx,.txt,.xls,.xlsx"
                    onChange={handleFileChange}
                    className="form-control"
                    id="documentsUpload"
                    placeholder="Select file"
                    multiple
                  />
                  {selectedVendor?.documents && selectedVendor.documents.filter(doc => doc !== "").length > 0 && (
                    <div className="mt-3">
                      <small className="text-muted">{t("shared.current_documents", "Current Documents:")}</small>
                      <div className="mt-1">
                        {selectedVendor.documents.filter(doc => doc !== "").map((docUrl, index) => {
                          const isPdf = docUrl?.toLowerCase().endsWith(".pdf");
                          const isImage = ["jpg", "jpeg", "png", "gif"].some(ext =>
                            docUrl?.toLowerCase().endsWith(ext)
                          );
                          const fileName = docUrl?.split("/").pop() || `${t("shared.document", "Document")} ${index + 1}`;

                          return (
                            <div key={index} className="d-flex align-items-center justify-content-between p-2 border rounded mb-1">
                              <div className="d-flex align-items-center">
                                <FeatherIcon
                                  icon={isPdf ? "file-text" : isImage ? "image" : "file"}
                                  size="16"
                                  className="text-primary me-2"
                                />
                                <small className="text-truncate" style={{ maxWidth: '200px' }}>
                                  {fileName}
                                </small>
                              </div>
                              <div className="d-flex align-items-center gap-1">
                                <a
                                  href={docUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="btn btn-outline-primary btn-sm"
                                  style={{ fontSize: '10px', padding: '2px 6px' }}
                                >
                                  {t("shared.view", "View")}
                                </a>
                                <button
                                  type="button"
                                  className="btn btn-outline-danger btn-sm"
                                  style={{ fontSize: '10px', padding: '2px 6px' }}
                                  onClick={() => removeDocument(index)}
                                >
                                  <FeatherIcon icon="x" size={12} />
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </Col>
              </Row>

              <div className="mt-4">
                {apiError && (
                  <div className="alert alert-danger">{apiError}</div>
                )}
                <button
                  type="submit"
                  className="btn btn-success w-lg transition-all hover-grow pulse-on-hover"
                  disabled={isSubmitting}
                >
                  {isSubmitting
                    ? t("shared.processing", "Processing...")
                    : selectedVendor?.id
                    ? t("shared.update", "Update")
                    : t("shared.add", "Add")}
                </button>
              </div>
            </form>
          </div>
        </motion.div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={deleteModal} toggle={() => setDeleteModal(false)}>
        <div className="modal-header">
          <h5 className="modal-title">{t("shared.confirm_delete", "Confirm Delete")}</h5>
          <button
            type="button"
            className="btn-close"
            onClick={() => setDeleteModal(false)}
            aria-label={t("shared.close", "Close")}
          ></button>
        </div>
        <div className="modal-body">
          <p>
            {t("business.vendors.delete_confirmation", "Are you sure you want to delete this vendor? This action cannot be undone.")}
          </p>
        </div>
        <div className="modal-footer">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setDeleteModal(false)}
          >
            {t("shared.cancel", "Cancel")}
          </button>
          <button
            type="button"
            className="btn btn-danger"
            onClick={handleDelete}
          >
            {t("shared.delete", "Delete")}
          </button>
        </div>
      </Modal>

      {/* Vendor Details Modal */}
      <VendorModal
        isOpen={vendorDetailsModal}
        toggle={toggleVendorDetailsModal}
        vendorData={selectedVendorForDetails}
        loading={false}
        onEditVendor={canModifyVendor(selectedVendorForDetails) ? (vendor) => {
          toggleVendorDetailsModal();
          toggleVendorModal(vendor);
        } : null}
      />

      <FileModal
        title={t("shared.vendors", "Vendors")}
        isOpen={fileModal}
        toggle={() => setFileModal(false)}
        data={vendors}
        importNeed={false}
        error={apiError}
        loading={loading}
        onImport={handleImport}
        onExport={handleExport}
        importLabel={t("business.vendors.export.import_label", "Import Vendors from Excel")}
        exportLabel={t("business.vendors.export.export_label", "Export Vendors to Excel")}
      />
    </div>
  );
};

export default Vendor;